// Fire the shared "session is dead → re-authenticate" signal at most once. AuthInit listens for this
// window event and hands the tab back to Keycloak (silent re-auth if the SSO session is still alive,
// the login page if not). Doing it via an event keeps the single redirect path in AuthInit (which owns
// the OIDC client) instead of importing oidc-client-ts here.
let deadSessionSignaled = false
function signalDeadSession() {
  if (deadSessionSignaled) return
  deadSessionSignaled = true
  window.dispatchEvent(new Event('sso:session-dead'))
}

// Under SSO, an API response can reveal a dead token in two shapes: an HTTP 401 (unauthenticated), or a
// legacy 200 body carrying `auth:false` (the V1 token-invalid signal the old `/auth/login` bounce keyed
// on — that bounce is neutered under SSO, so nothing would redirect otherwise). 403 is deliberately NOT
// treated as dead: that's an authorization failure (logged in, lacks permission), not a lost session.
// Only act when we BELIEVED we were logged in (a token is present) to avoid firing on public pre-auth
// pages.
function looksUnauthenticated(status: number | undefined, data: any): boolean {
  if (process.env.REACT_APP_SSO_ENABLED !== '1') return false
  if (!localStorage.getItem('token')) return false
  // ONLY act on a session that was already fully established (set by AuthInit once the token bridge
  // completes). During initial login/bootstrap the app deliberately tolerates a failed getUser and boots
  // anyway; firing dead-session recovery there would pre-empt that graceful path and loop the OIDC
  // callback (white screen). Recovery is for sessions that die AFTER bootstrap (e.g. an email change).
  if (!(window as any).__ssoBootstrapped) return false
  return status === 401 || data?.auth === false
}

export default function setupAxios(axios: any, store: any) {
  axios.defaults.headers.Accept = 'application/json'

  // Dead-session detection on the RESPONSE path (see looksUnauthenticated). Success bodies can still
  // carry `auth:false`; error responses carry the 401 status.
  axios.interceptors.response.use(
    (response: any) => {
      if (looksUnauthenticated(response?.status, response?.data)) signalDeadSession()
      return response
    },
    (error: any) => {
      if (looksUnauthenticated(error?.response?.status, error?.response?.data)) signalDeadSession()
      return Promise.reject(error)
    }
  )

  axios.interceptors.request.use(
    (config: any) => {
      if (process.env.REACT_APP_SSO_ENABLED === '1') {
        // Under SSO, read the token from localStorage at REQUEST time, not from redux. Keycloak's
        // access token is short-lived (dev = 120s) and automaticSilentRenew rotates it; AuthInit writes
        // each fresh token to localStorage('token'). Reading it live here means the interceptor never
        // sends a stale/expired token (redux `accessToken` was only set once at login → "Failed to
        // authenticate token" after the first expiry). Sent as `x-access-token` (V2's dual-mode reads
        // it; both V1 and V2 allow it in CORS — V2 does NOT allow `Authorization`).
        const token = localStorage.getItem('token')
        if (token) config.headers['x-access-token'] = token
        return config
      }

      const {
        auth: {accessToken},
      } = store.getState()
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`
      }

      return config
    },
    (err: any) => Promise.reject(err)
  )
}
